import classes from "./MainTop.module.css";
import { useTranslation } from "react-i18next";
import { BsPrinter } from "react-icons/bs";
import { Employee, SearchLabel, Unit } from "../../../contexts/interfaces";
import FormButton from "../../Assets/FormButton/FormButton";
import { useRoutesContext } from "../../../contexts/RoutesContext";
import createCompilationFile from "../../../scripts/createCompilationFile";
import Select from "react-select";
import { useEffect, useState } from "react";
import { convertDate } from "../../../utils/convertDate";
import { StylesConfig } from "react-select";
import { GroupBase } from "react-select"; // If using grouped options
import { CSSObjectWithLabel } from "react-select"; // To properly type the provided object

interface Props {
  selectedUnit: Unit;
  setSelectedUnit: (val: Unit) => void;
  unitData: Unit[];
  employeesInUnit: Employee[];
}

const MainTop = ({
  selectedUnit,
  setSelectedUnit,
  unitData,
  employeesInUnit,
}: Props) => {
  const { routes } = useRoutesContext();

  const { t } = useTranslation();

  const [searchOptions, setSearchOptions] = useState<SearchLabel[]>();

  useEffect(() => {
    setSearchOptions(
      unitData.map((unit) => {
        return {
          label: `${unit.name}`,
          value: String(unit.id),
        };
      })
    );
  }, [unitData]);

  const createXlsx = () => {
    // Filename is name of unit, spaces replaced with underscore
    const fileName = selectedUnit.name.replace(/ /g, "_");

    // Create new json-style object for compilation function
    const dataToExport = employeesInUnit.map((emp) => {
      return {
        [t("compilationFile.id")]: emp.employeeId,
        [t("compilationFile.name")]: `${emp.firstName} ${emp.lastName}`,
        [t("compilationFile.jobTitle")]: emp.jobTitle,
        [t("compilationFile.unit")]: emp.expenseUnitName,
        [t("compilationFile.formStatus")]: t(
          `compilationFile.formStatus_${emp.formStatus}`
        ),
        [t("compilationFile.permitsValidUntil")]:
          convertDate(emp.permitsValidUntil) || "N/A",
        [t("compilationFile.medicineOrder")]:
          emp.permits.findIndex((permit) => permit.type === "medicineOrder") !==
          -1
            ? "X"
            : "",
        [t("compilationFile.medicineDistributionToDispenser")]:
          emp.permits.findIndex(
            (permit) => permit.type === "medicineDistributionToDispenser"
          ) !== -1
            ? "X"
            : "",
        [t("compilationFile.medicineNatural")]:
          emp.permits.findIndex(
            (permit) => permit.type === "medicineNatural"
          ) !== -1
            ? "X"
            : "",
        [t("compilationFile.medicineSC")]:
          emp.permits.findIndex((permit) => permit.type === "medicineSC") !== -1
            ? "X"
            : "",
        [t("compilationFile.medicineIM")]:
          emp.permits.findIndex((permit) => permit.type === "medicineIM") !== -1
            ? "X"
            : "",
        [t("compilationFile.medicineActualDrugs")]:
          emp.permits.findIndex(
            (permit) => permit.type === "medicineActualDrugs"
          ) !== -1
            ? "X"
            : "",
        [t("compilationFile.medicineSCCannula")]:
          emp.permits.findIndex(
            (permit) => permit.type === "medicineSCCannula"
          ) !== -1
            ? "X"
            : "",
        [t("compilationFile.medicinePKV")]:
          emp.permits.findIndex((permit) => permit.type === "medicinePKV") !==
          -1
            ? "X"
            : "",
        [t("compilationFile.medicinePKVDistributed")]:
          emp.permits.findIndex(
            (permit) => permit.type === "medicinePKVDistributed"
          ) !== -1
            ? "X"
            : "",
        [t("compilationFile.medicineVaccination")]:
          emp.permits.findIndex(
            (permit) => permit.type === "medicineVaccination"
          ) !== -1
            ? "X"
            : "",
        [t("compilationFile.medicinePEG")]:
          emp.permits.findIndex((permit) => permit.type === "medicinePEG") !==
          -1
            ? "X"
            : "",
        [t("compilationFile.medicinePO")]:
          emp.permits.findIndex((permit) => permit.type === "medicinePO") !== -1
            ? "X"
            : "",
      };
    });

    // Send filename and data to compilation function
    createCompilationFile(fileName, dataToExport);
  };

  const customStyles: StylesConfig<
    SearchLabel,
    false,
    GroupBase<SearchLabel>
  > = {
    control: (provided: CSSObjectWithLabel) => ({
      ...provided,
      border: "none",
      cursor: "pointer",
      paddingRight: "2px",
      minWidth: "150px",
      backgroundColor: "#f9f9f9",
    }),
    menu: (provided: CSSObjectWithLabel) => ({
      ...provided,
      width: "auto",
      minWidth: "150px",
      zIndex: 999,
    }),
    option: (provided: CSSObjectWithLabel) => ({
      ...provided,
      whiteSpace: "nowrap",
    }),
    singleValue: (provided: CSSObjectWithLabel) => ({
      ...provided,
      width: "auto",
      maxWidth: "150px", // Max width with consideration for padding
      whiteSpace: "nowrap", // Prevents text from wrapping
      overflow: "hidden", // Hides overflow text
      textOverflow: "ellipsis", // Adds ellipsis for overflowing text
    }),
  };

  return (
    <div className={classes.mainTopRow}>
      <div className={classes.select}>
        {selectedUnit && (
          <div className={classes.selectInnerText}>
            {`${t("mainTop.selectUnitInnerText")}:`}
          </div>
        )}
        {searchOptions && (
          <Select
            defaultValue={searchOptions.find(
              (option) => Number(option.value) === selectedUnit.id
            )}
            isSearchable={false}
            isClearable={false}
            styles={customStyles}
            options={searchOptions}
            onChange={(e) =>
              e &&
              setSelectedUnit({
                ...(unitData.find((unit) => unit.id === Number(e.value)) ||
                  unitData[0]),
              })
            }
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
            }}
          />
        )}
      </div>

      <div className={classes.buttonPadder}>
        <FormButton redirectUrl={`/${routes.form}`} formExists={false} />
      </div>

      <div className={classes.button} onClick={() => createXlsx()}>
        <BsPrinter className={classes.icon} />
        <div className={classes.buttonText}>{t("mainTop.printButtonText")}</div>
      </div>
    </div>
  );
};

export default MainTop;
